import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import moment from 'moment/moment';

import { DocumentService } from '@core/api/document.service';
import { GisService } from '@core/api/gis.service';
import { KmlForestryUploadService } from '@core/api/kml-forestry-upload.service';
import { SiteService } from '@core/api/site.service';
import { SpinnerService } from '@shared/spinner/spinner.service';
import * as fromApp from '@store';
import { Site } from '@shared/models';
import { ToastService } from '@shared/services/toast.service';
import { ToastLevel } from '@shared/models/toast-message.module';

export interface DialogData {
  site: Site;
}

@Component({
  selector: 'fc-dialog-split-site',
  templateUrl: './dialog-split-site.component.html',
  styleUrls: ['./dialog-split-site.component.scss'],
  standalone: false
})
export class DialogSplitSiteComponent implements OnInit {
  @ViewChild('kmlFileUpload', { static: false }) kmlFileUpload: ElementRef;
  kmlDocSelectForm: UntypedFormGroup;
  errorMessage = null;
  minEffectiveDate: Date = new Date();
  maxEffectiveDate: Date = new Date();
  parsedKml: any[] = [];
  displayedFileNames: string[] = [];

  constructor(
    public dialogRef: MatDialogRef<DialogSplitSiteComponent>,
    readonly http: HttpClient,
    readonly formBuilder: UntypedFormBuilder,
    readonly siteService: SiteService,
    readonly documentService: DocumentService,
    readonly spinnerService: SpinnerService,
    readonly gisService: GisService,
    readonly kmlForestryUploadService: KmlForestryUploadService,
    readonly store: Store<fromApp.AppState>,
    readonly toastService: ToastService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {
    this.minEffectiveDate = data.site.paymentPeriods
      .filter(p => !p.paymentId)
      .sort((a, b) => a.sequence - b.sequence)[0].startDate;
    if (data.site.submittedDate > this.minEffectiveDate) {
      this.minEffectiveDate = data.site.submittedDate;
    }
    this.minEffectiveDate = new Date(this.minEffectiveDate + 'T00:00:00');
    this.maxEffectiveDate.setHours(0);
  }

  ngOnInit() {
    this.kmlDocSelectForm = this.formBuilder.group({
      effectiveDate: ['', Validators.required],
      createLeftover: [true, Validators.required],
    });
  }

  validateKMLFile = (file: any) => {
    const fileExtension = file.name.substring(file.name.length - 4);
    this.spinnerService.show('app-spinner', 'Validating KML');
    switch (fileExtension) {
      case '.kml':
        this.kmlForestryUploadService
          .parseKmlDocument(file, false)
          .then(parsedKml => {
            this.parsedKml.push(parsedKml);
            this.displayedFileNames.push(file.name);
          })
          .finally(() => {
            this.spinnerService.hide('app-spinner');
          });
        break;
      case '.kmz':
        this.kmlForestryUploadService
          .parseKmzDocument(file, false)
          .then(parsedKml => {
            this.parsedKml.push(parsedKml);
            this.displayedFileNames.push(file.name);
          })
          .finally(() => {
            this.spinnerService.hide('app-spinner');
          });
        break;
      default:
        this.toastService.sendMessage('Only .kml and .kmz files are supported.', ToastLevel.WARN);
        this.spinnerService.hide('app-spinner');
    }
  };

  onClickKmlUpload(): void {
    const kmlFileUpload = this.kmlFileUpload.nativeElement;
    kmlFileUpload.onchange = () => {
      if (kmlFileUpload.files.length > 0) {
        this.displayedFileNames = [];
        this.parsedKml = [];
        for (const file of kmlFileUpload.files) {
          this.validateKMLFile(file);
        }
      }
    };
    kmlFileUpload.click();
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  applyKML() {
    this.dialogRef.close({
      effectiveDate: moment(this.kmlDocSelectForm.get('effectiveDate').value, true).format('YYYY-MM-DD'),
      newKMLs: this.parsedKml,
      createLeftover: this.kmlDocSelectForm.get('createLeftover').value,
    });
  }
}
